/**
 * Which id a chat viewer OWNS messages as, decided from the three ids a browser can be holding at once.
 *
 * The Durable Object stamps every persisted message with the subject it verified at the WebSocket upgrade -- for a guest
 * that is the `gid` inside their chats session token. Ownership (`computeIsOwn`) and optimistic-echo settling
 * (`deduplicateMessages`) both compare against that value, so anything else here misattributes a viewer's own messages
 * and doubles their sends.
 *
 * `isLoggedIn` is deliberately NOT an input. A real returning-guest browser reported `isLoggedIn: true` while every id
 * slot held a guest id, so gating the credential behind it hands back the shell id for exactly the guests this exists to
 * serve. The ID SHAPE is the discriminator instead.
 *
 * Precedence, and why each step is where it is:
 *  1. A shell id WITHOUT the guest prefix is a real account -- it wins outright, so an authenticated viewer carrying a
 *     stale guest token from a signed-out session is never resolved to that guest.
 *  2. Otherwise the credential subject, because that is the value the DO actually stamped.
 *  3. Then a guest-shaped shell id, then a guest-shaped `rawUserId` -- the fallbacks for a guest whose token has not
 *     been minted yet (it arrives only with the bootstrap create response).
 *  4. Otherwise '' -- no subject, and the caller renders nothing as owned rather than guessing.
 */

import { GUEST_ID_PREFIX, isSignedInUserId } from '@federa/const/auth-session';
import { isValidString, stringOrEmpty } from '@federa/const/string-guards';

interface ViewerSubjectCandidates {
	/** `userId` published on the shell bridge; a guest-shaped value here is the OTHER surface's identity, not this one's. */
	shellUserId?: string | null;
	/** `gid` read off the stored chats guest session token -- the subject the DO stamps. */
	credentialSubject?: string | null;
	/** Shell `rawUserId`, the unstripped spelling, used only as the last guest fallback. */
	rawUserId?: string | null;
}

function guestShaped(value: string | null | undefined): string {
	return typeof value === 'string' && value.startsWith(GUEST_ID_PREFIX) ? value : '';
}

export function resolveViewerSubject(candidates: ViewerSubjectCandidates): string {
	const { shellUserId, credentialSubject, rawUserId } = candidates;
	const shellId = stringOrEmpty(shellUserId);
	if (isSignedInUserId(shellId)) return shellId;
	if (isValidString(credentialSubject)) return credentialSubject;
	return guestShaped(shellId) || guestShaped(rawUserId);
}
